import { auth } from "@/config/firebase";
import { config } from "@/config/env";
import { generateTraceparent } from "./correlation";

/**
 * Centralized fetch wrapper that handles cross-cutting concerns for every
 * SPA → backend HTTP call:
 *
 *   - URL building: relative paths get prefixed with `config.apiUrl` (the
 *     LB-fronted host in cloud envs, empty in dev so Vite's proxy handles it).
 *   - Auth: Firebase ID token attached as `Authorization: Bearer <token>` if
 *     not already set by the caller and a user is signed in.
 *   - Org context: `x-organization-id` header set from sessionStorage when a
 *     specific org is selected.
 *   - Tracing: W3C `traceparent` header set on every request, sharing the
 *     per-action trace-id and minting a fresh span-id (see ./correlation.ts).
 *
 * The signature mirrors `fetch()` so call sites can do a near-1:1 swap. The
 * caller can override any auto-injected header by passing it in `init.headers`.
 *
 * Why centralize: pre-Tier-1 the SPA had ~30 bare `fetch()` sites, each
 * hand-rolling the auth + org + URL pattern (and most omitting any tracing
 * header at all). Centralizing means traceparent is impossible to forget on a
 * new fetch site, and means the eventual move to OpenTelemetry browser SDK
 * (audit doc Phase 4 / "Tier 2") only has to wrap one function.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${config.apiUrl}${path}`;

  // Build the header set without mutating the caller's input.
  const headers = new Headers(init.headers ?? {});

  if (!headers.has("authorization") && !headers.has("Authorization")) {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
  }

  if (
    !headers.has("x-organization-id") &&
    !headers.has("X-Organization-Id")
  ) {
    const orgId = sessionStorage.getItem("currentOrganizationId");
    if (orgId && orgId !== "all") {
      headers.set("x-organization-id", orgId);
    }
  }

  // Always stamp traceparent — even if the caller set one, theirs wins
  // (allows special cases like SSE retries that want to share an upstream
  // trace context). Otherwise mint fresh span-id under the current trace.
  if (!headers.has("traceparent")) {
    headers.set("traceparent", generateTraceparent());
  }

  return fetch(url, { ...init, headers });
}
